import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Send, Bot, Loader2, Zap, Brain, Database, User as UserIcon, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

interface ChatMessage {
    sender: 'user' | 'bot';
    text: string;
    options?: { label: string; action: string; value?: string }[];
}

interface ChatProps {
    onOpenRegister?: () => void; 
    onOpenLogin?: () => void;
}

const courseData = {
    "Web Development": ["Java Full Stack", "MERN Full Stack", "Python Full Stack", "ReactJS Development"],
    "App Development": ["Android", "IOS", "Flutter"],
    "Programming": ["Core Java", "Python", "C++", "Spring Boot"],
    "Database": ["SQL", "MySQL", "MongoDB"],
    "Cloud & DevOps": ["AWS", "Azure", "DevOps"]
};

const AIChatExpert: React.FC<ChatProps> = ({ onOpenRegister, onOpenLogin }) => {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);

    const [chatStep, setChatStep] = useState('INIT');
    const [leadData, setLeadData] = useState({ name: '', email: '', mobile: '' });

    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [messages]);

    useEffect(() => {
        if (messages.length === 0) {
            startConversation();
        }
    }, [user]);

    const startConversation = () => {
        let initialMsgs: ChatMessage[] = [];
        if (user) {
            initialMsgs.push({ sender: 'bot', text: `Welcome back, ${user.name}! How can I assist you today?` });
            setChatStep('MENU');
            setTimeout(() => { setMessages(prev => [...prev, getMenuOptions()]); }, 500);
        } else {
            initialMsgs.push({ sender: 'bot', text: "Hello! I'm your AI Career Expert. To assist you better, may I know your Name?" });
            setChatStep('NAME');
        }
        setMessages(initialMsgs);
    };

    const getMenuOptions = (): ChatMessage => ({
        sender: 'bot', text: "Please select an option:",
        options: [
            { label: "📝 Register Query", action: "REGISTER_QUERY" },
            { label: "🔍 Search Course", action: "SEARCH_COURSE" },
            { label: "👤 Make My Profile", action: "MAKE_PROFILE" }
        ]
    });

    const processUserInput = async (text: string) => {
        const newMsg: ChatMessage = { sender: 'user', text };
        setMessages(prev => [...prev, newMsg]);
        setLoading(true);

        await new Promise(r => setTimeout(r, 800));
        let botResponse: ChatMessage = { sender: 'bot', text: '' };

        try {
            if (chatStep === 'NAME') {
                setLeadData(prev => ({ ...prev, name: text }));
                botResponse.text = `Nice to meet you, ${text}. Please provide your Email ID.`;
                setChatStep('EMAIL');
            } 
            else if (chatStep === 'EMAIL') {
                if (!text.includes('@')) { botResponse.text = "That doesn't look like a valid email. Please try again."; } 
                else { setLeadData(prev => ({ ...prev, email: text })); botResponse.text = "Great! Lastly, please share your Mobile Number."; setChatStep('MOBILE'); }
            } 
            else if (chatStep === 'MOBILE') {
                const mobile = text.replace(/\D/g, "");
                if (mobile.length < 10) { botResponse.text = "Please enter a valid 10-digit mobile number."; } 
                else {
                    const finalData = { ...leadData, mobile: text };
                    setLeadData(finalData);
                    try {
                        const res = await fetch('https://www.projectinfotech.in/api/chat/save-lead', {
                            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(finalData)
                        });
                        if (res.ok) {
                            const data = await res.json();
                            login(data.token, { name: finalData.name, email: finalData.email });
                            botResponse.text = "Thank you! Your details have been saved successfully. How can I help you today?";
                            setMessages(prev => [...prev, botResponse]); 
                            setTimeout(() => { setMessages(prev => [...prev, getMenuOptions()]); }, 500);
                            setChatStep('MENU'); setLoading(false); return; 
                        } else {
                            botResponse.text = "Something went wrong saving your details, but let's continue.";
                            setChatStep('MENU');
                        }
                    } catch (err) {
                        botResponse.text = "Connection failed. Please check your internet or try again later.";
                        setChatStep('MENU');
                    }
                }
            }
            else if (chatStep === 'MENU') { botResponse.text = "Please select an option from the buttons above."; }
            setMessages(prev => [...prev, botResponse]);
        } catch (error) { setMessages(prev => [...prev, { sender: 'bot', text: "Error processing request." }]); } 
        finally { setLoading(false); }
    };

    const handleOptionClick = async (action: string, value?: string) => {
        setLoading(true); await new Promise(r => setTimeout(r, 600));
        if (action === 'REGISTER_QUERY') {
            if (onOpenRegister) onOpenRegister();
            setMessages(prev => [...prev, { sender: 'bot', text: "I've opened the registration form for you." }]);
        } 
        else if (action === 'MAKE_PROFILE') {
            if (user) { setMessages(prev => [...prev, { sender: 'bot', text: "You are already logged in!" }]); } 
            else { if(onOpenLogin) onOpenLogin(); setMessages(prev => [...prev, { sender: 'bot', text: "Opening login window..." }]); }
        } 
        else if (action === 'SEARCH_COURSE') {
            setMessages(prev => [...prev, {
                sender: 'bot', text: "Which domain are you interested in?",
                options: Object.keys(courseData).map(domain => ({ label: domain, action: 'SELECT_DOMAIN', value: domain }))
            }]);
            setChatStep('DOMAIN_SELECT');
        }
        else if (action === 'SELECT_DOMAIN' && value) {
            const courses = courseData[value as keyof typeof courseData] || [];
            setMessages(prev => [...prev, {
                sender: 'bot', text: `Here are the courses under ${value}:`,
                options: courses.map(c => ({ label: c, action: 'SELECT_COURSE', value: c }))
            }]);
            setChatStep('COURSE_SELECT');
        }
        else if (action === 'SELECT_COURSE' && value) {
            let path = "";
            if(value.includes("Java")) path = "/java-full-stack";
            else if(value.includes("Python")) path = "/python-full-stack";
            else if(value.includes("MERN")) path = "/mern-full-stack";
            else path = "/"; 
            setMessages(prev => [...prev, { sender: 'bot', text: `Redirecting you to ${value}...` }]);
            setTimeout(() => { navigate(path); }, 1500);
        }
        setLoading(false);
    };

    const handleSend = (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() || loading) return;
        const txt = input; setInput(''); processUserInput(txt);
    };

    const MessageItem: React.FC<ChatMessage> = ({ sender, text, options }) => (
        <div className={`flex flex-col mb-4 ${sender === 'user' ? 'items-end' : 'items-start'} animate-fade-in-up`}>
            <div className={`max-w-[85%] p-3 rounded-xl shadow-sm ${sender === 'user' ? 'bg-blue-600 text-white rounded-br-none' : 'bg-white text-gray-800 border border-gray-200 rounded-tl-none'}`}>
                <span className="text-sm whitespace-pre-wrap">{text}</span>
            </div>
            {options && (
                <div className="flex flex-wrap gap-2 mt-2 max-w-[90%] animate-fade-in">
                    {options.map((opt, idx) => (
                        <button key={idx} onClick={() => handleOptionClick(opt.action, opt.value)} aria-label={`Select ${opt.label}`} className="bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 px-3 py-1.5 rounded-full text-xs font-semibold transition-all shadow-sm transform hover:scale-105">
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 py-16 pt-32">
            <Helmet>
                <title>AI Career Expert | Free IT Guidance | PIT</title>
                <meta name="description" content="Chat with our AI Career Expert to get personalized guidance on Web Development, Data Science, App Dev, and Cloud courses based on your profile." />
            </Helmet>

            <div className="text-center mb-8 max-w-4xl mx-auto px-4">
                <h1 className="text-3xl font-extrabold text-blue-900 mb-2 flex items-center justify-center">
                    <Brain className="w-8 h-8 mr-2 text-orange-500" aria-hidden="true" /> AI Career Assistant
                </h1>
                <p className="text-gray-600 text-sm">Real-time guidance for your software engineering career path.</p>
            </div>

            <div className="max-w-3xl mx-auto px-4">
                <div className="bg-white rounded-2xl shadow-xl border overflow-hidden h-[600px] flex flex-col">
                    <div className="p-4 bg-gradient-to-r from-blue-700 to-blue-900 text-white flex justify-between items-center shadow-md">
                        <div className="flex items-center gap-2">
                            <Bot className="w-6 h-6" aria-hidden="true" />
                            <div>
                                <h2 className="font-bold text-sm">PIT AI Expert</h2>
                                <p className="text-xs text-blue-200 flex items-center gap-1">
                                    <span className="w-2 h-2 bg-green-400 rounded-full"></span> Online
                                </p>
                            </div>
                        </div>
                        <button onClick={() => { setMessages([]); setChatStep('INIT'); startConversation(); }} aria-label="Restart Chat" className="text-white/80 hover:text-white" title="Restart Chat">
                            <RefreshCw size={18} />
                        </button>
                    </div>

                    <div className="flex-grow overflow-y-auto p-4 bg-gray-50 scroll-smooth" role="log" aria-live="polite">
                        {messages.map((msg, i) => (<MessageItem key={i} {...msg} />))}
                        {loading && (
                            <div className="flex justify-start mb-4">
                                <div className="bg-gray-100 p-3 rounded-xl rounded-tl-none flex items-center gap-2">
                                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" aria-hidden="true" />
                                    <span className="text-xs text-gray-500">Thinking...</span>
                                </div>
                            </div>
                        )}
                        <div ref={chatEndRef} />
                    </div>

                    <form onSubmit={handleSend} className="p-3 bg-white border-t border-gray-200 flex items-center gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={chatStep === 'NAME' ? "Enter your Name..." : chatStep === 'EMAIL' ? "Enter your Email..." : chatStep === 'MOBILE' ? "Enter Mobile Number..." : "Type your message..."}
                            disabled={loading}
                            aria-label="Chat input field"
                            className="flex-grow p-3 bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                        <button type="submit" disabled={loading || !input.trim()} aria-label="Send Message" className="p-3 bg-orange-500 text-white rounded-full hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all">
                            <Send size={18} aria-hidden="true" />
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AIChatExpert;